import React from 'react';
import { formatCurrencyShort } from '@vanilla/utils/Utils.js';
import { COMMISSION_CONTRIBUTOR_ROLE_ORDER, COMMISSION_CONTRIBUTOR_ROLES } from '@vanilla/constants/CommissionContributorRoles.js';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DealFormSelect } from './DealFormSelect.jsx';
import DealCommissionSchedulePanel from './DealCommissionSchedulePanel.jsx';
import { FieldHelpIcon } from '@/components/ui/FieldHelpIcon.jsx';

export default function DealFormCommissionFormPanel({
    form,
    setField,
    users,
    recipientUsers,
    commissionEstimate,
    commissionAssignedPct,
    splitEvenly,
    addSplit,
    updateSplit,
    removeSplit,
}) {
    return (
        <div className="premium-card" id="sectionCommissionCard" data-stepper-panel="commission">
            <div className="premium-card-header">
                <div className="premium-card-header-group">
                    <h3 className="premium-card-title"><span className="premium-card-number">4</span> Commission</h3>
                    <p className="premium-card-subtitle">Set eligibility, calculation, and recipient splits.</p>
                </div>
            </div>

            <fieldset className="form-section" id="dealCommissionSection">
                <div className="deal-form-grid-1">
                    <div className="form-group form-group-checkbox full-width commission-eligibility-check toggle-row">
                        <span></span>
                        <div className="toggle-label-group">
                            <label htmlFor="dealCommissionEligible">Eligible for Commission <FieldHelpIcon fieldId="dealCommissionEligible" /></label>
                            <Switch
                                id="dealCommissionEligible"
                                checked={!!form.commissionEligible}
                                onCheckedChange={(v) => setField('commissionEligible', v)}
                            />
                        </div>
                    </div>
                </div>

                {form.commissionEligible && (
                    <div id="dealCommissionConfigArea" className="flex flex-col gap-4">
                        <div className="deal-form-grid-2">
                            <div className="form-group">
                                <label htmlFor="dealCommissionModel">Calculation <FieldHelpIcon fieldId="dealCommissionModel" /></label>
                                <DealFormSelect
                                    id="dealCommissionModel"
                                    value={form.commissionModel}
                                    allowEmpty={false}
                                    onValueChange={(v) => setField('commissionModel', v)}
                                    options={[
                                        { value: 'percentage', label: 'Percentage' },
                                        { value: 'fixed_amount', label: 'Fixed Amount' },
                                    ]}
                                />
                            </div>
                            {form.commissionModel === 'percentage' ? (
                                <div className="form-group" id="dealCommissionRateGroup" data-field="dealCommissionRate">
                                    <label htmlFor="dealCommissionRate">Rate <FieldHelpIcon fieldId="dealCommissionRate" /></label>
                                    <div className="input-adorned"><Input unstyled type="text" inputMode="decimal" id="dealCommissionRate" className="form-input" min="0" max="100" step="0.01" placeholder="Enter commission rate" value={form.commissionRate} onChange={(e) => setField('commissionRate', e.target.value.replace(/[^0-9.]/g, ''))} /><span className="input-suffix">%</span></div>
                                </div>
                            ) : (
                                <div className="form-group" id="dealCommissionFixedGroup" data-field="dealCommissionFixed">
                                    <label htmlFor="dealCommissionFixed">Pool Amount ($) <FieldHelpIcon fieldId="dealCommissionFixed" /></label>
                                    <div className="input-adorned"><span className="input-prefix">$</span><Input unstyled type="text" inputMode="decimal" id="dealCommissionFixed" step="0.01" className="form-input" placeholder="Enter pool amount" value={form.commissionFixedAmount} onChange={(e) => setField('commissionFixedAmount', e.target.value.replace(/[^0-9.]/g, ''))} /></div>
                                </div>
                            )}
                        </div>
                        <div className="deal-form-grid-2">
                            <div className="form-group">
                                <label htmlFor="dealCommissionBasis">Commission Basis <FieldHelpIcon fieldId="dealCommissionBasis" /></label>
                                <DealFormSelect
                                    id="dealCommissionBasis"
                                    value={form.commissionBasis || 'schedule'}
                                    allowEmpty={false}
                                    onValueChange={(v) => setField('commissionBasis', v)}
                                    options={[
                                        { value: 'deal_value', label: 'Deal Value' },
                                        { value: 'schedule', label: 'Revenue Schedule' },
                                    ]}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="dealPayoutMethod">Payout Method <FieldHelpIcon fieldId="dealPayoutMethod" /></label>
                                <DealFormSelect
                                    id="dealPayoutMethod"
                                    value={form.payoutMethod || 'per_schedule'}
                                    allowEmpty={false}
                                    onValueChange={(v) => setField('payoutMethod', v)}
                                    options={[
                                        { value: 'one_time', label: 'One-Time' },
                                        { value: 'per_schedule', label: 'Per Schedule' },
                                        { value: 'custom', label: 'Custom' },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="commission-estimate-card" id="dealCommissionEstimateCard">
                            <div className="commission-estimate-main">
                                <div className="commission-estimate-label">Estimated Commission</div>
                                <div className="commission-estimate-value" id="dealCommissionEstimateDisplay">{formatCurrencyShort(commissionEstimate)}</div>
                                <div className="commission-estimate-note" id="dealCommissionEstimateNote">{form.commissionBasis==='deal_value' ? 'Calculated from deal value' : form.payoutMethod==='one_time' ? 'One-time from deal value' : form.commissionModel==='fixed_amount' ? 'Fixed pool prorated by schedule' : 'Calculated from revenue schedules'}</div>
                            </div>
                            <div className="commission-estimate-meta" id="dealCommissionEstimateMeta">
                                <div className="commission-estimate-meta-item">
                                    <span className="meta-value" id="estRecipientsCount">{(form.commissionSplits || []).filter((s) => s.userId).length}</span>
                                    <span className="meta-label">Recipients</span>
                                </div>
                                <div className="commission-estimate-meta-item">
                                    <span className="meta-value" id="estAssignedPct">{commissionAssignedPct}%</span>
                                    <span className="meta-label">Assigned</span>
                                </div>
                                <div className="commission-estimate-meta-item">
                                    <span className="meta-value" id="estRemainingPct">{Math.max(0, 100 - commissionAssignedPct)}%</span>
                                    <span className="meta-label">Remaining</span>
                                </div>
                            </div>
                        </div>

                        <Separator className="commission-section-divider" />

                        <div className="commission-split-section" data-field="dealCommissionSplit">
                            <div className="commission-recipients-header">
                                <h4>Recipients</h4>
                                <div className="recipients-header-actions">
                                    <Button type="button" className="btn btn-outline-primary btn-sm" variant="outline" size="sm" id="dealSplitEvenlyBtn" onClick={splitEvenly}>Split Evenly</Button>
                                    <Button type="button" className="btn btn-primary btn-sm" variant="default" size="sm" id="dealAddSplitBtn" onClick={addSplit}>+ Add Recipient</Button>
                                </div>
                            </div>
                            <div id="dealCommissionSplitRows">
                                {(form.commissionSplits || []).map((s, i) => (
                                    <div className="deal-form-inline-pair" key={i} style={{ alignItems: 'center', marginBottom: 8 }}>
                                        <div className="form-group" style={{ flex: 2 }}>
                                            <DealFormSelect
                                                value={s.userId}
                                                placeholder="Select user"
                                                onValueChange={(v) => updateSplit(i, { userId: v })}
                                                options={recipientUsers.map((u) => ({ value: u.id, label: u.name || u.email || u.id }))}
                                            />
                                        </div>
                                        <div className="form-group" style={{ flex: 2 }}>
                                            <DealFormSelect
                                                value={s.role}
                                                allowEmpty={false}
                                                onValueChange={(v) => updateSplit(i, { role: v })}
                                                options={COMMISSION_CONTRIBUTOR_ROLE_ORDER.map((r) => ({ value: r, label: COMMISSION_CONTRIBUTOR_ROLES[r] }))}
                                            />
                                        </div>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <div className="input-adorned"><Input unstyled type="text" inputMode="decimal" className="form-input" min="0" max="100" value={s.splitPct} onChange={(e) => updateSplit(i, { splitPct: e.target.value.replace(/[^0-9.]/g, '') })} /><span className="input-suffix">%</span></div>
                                        </div>
                                        <button type="button" className="card-action-btn link-danger" onClick={() => removeSplit(i)}>×</button>
                                    </div>
                                ))}
                            </div>
                            <div className="split-progress-container" id="dealSplitProgressContainer">
                                <Progress
                                    id="dealSplitProgressBar"
                                    className="split-progress-bar"
                                    value={Math.min(commissionAssignedPct, 100)}
                                    indicatorClassName="split-progress-fill"
                                />
                                <div className="split-progress-label" id="dealSplitProgressLabel">
                                    <span className="progress-assigned" id="splitAssignedText">{commissionAssignedPct}% assigned</span>
                                    <span className="progress-remaining" id="splitRemainingText">{Math.max(0, 100 - commissionAssignedPct)}% remaining</span>
                                </div>
                            </div>
                            {commissionAssignedPct !== 100 && commissionAssignedPct !== 0 && (
                                <div className="field-error" style={{display:'block', marginTop:4}}>{commissionAssignedPct > 100 ? 'Total exceeds 100% — reduce splits' : `Total must equal 100% — ${Math.abs(100-commissionAssignedPct).toFixed(2)}% remaining` } (e.g. 40%+60%=valid, 33.33+33.33+33.34=valid)</div>
                            )}
                        </div>
                    </div>
                )}
            </fieldset>

            <DealCommissionSchedulePanel
                dealId={form.id}
                commissionEligible={form.commissionEligible}
                users={users}
            />
        </div>
    );
}
